import React, { Component } from "react";
import { browserHistory } from "react-router";
import { connect } from "react-redux";
import cookie from "react-cookie";
import Config from "../config/main";
import { RestoreLogin } from "../actions/login";
import { LoadCurrencyConfig, LoadPartnerConfig, SwarmConfigData } from "../actions/initialData";
import {
	ClosePopup,
	OpenPopup,
	UIOpen,
	UISetCurrentRouteType,
	UISetLastRouteType,
	UISetPreviousPath,
} from "../actions/ui";
import Helpers from "../helpers/helperFunctions";
import moment from "moment";
import { langMapping } from "../constants/momentLocale";
import { t } from "../helpers/translator";
import { StoreHashParams } from "../actions/hashParams";
import PropTypes from "prop-types";
import { BetslipClear, BetslipAdd, AddBetSlipEventFromUrl } from "../../dev/actions/betslip";
import { calculateBirthDateDiff } from "../helpers/routerListeners/requireAuth";
import { GamesFavoritesClear } from "../actions/favorites";
import { createSelector } from "reselect";
import routeHelper from "../helpers/routeHelper";
import { registerLocale } from "react-datepicker";

function mapStateToProps() {
	return createSelector(
		[
			state => state.appReady,
			state => JSON.stringify(state.persistentUIState.hashParams),
			state => state.user.profile,
			state => JSON.stringify(state.preferences),
			state => state.user,
			state => state.uiState.lastRouteType,
			state => state.swarmConfigData && state.swarmConfigData.cloudAvailable,
		],
		(appReady, hashParams, profile, preferences, user, lastRouteType, cloudAvailable) => {
			return {
				appReady,
				hashParams: JSON.parse(hashParams),
				profile,
				preferences: JSON.parse(preferences),
				user,
				lastRouteType,
				cloudAvailable,
			};
		}
	);
}

function getRouteType(loc) {
	return {
		"": "sport",
		live: "sport",
		prematch: "sport",
		asian_live: "sport",
		asian_prematch: "sport",
		asian_today: "sport",
		game: "sport",
		history: "sport",
		casino: "casino",
		"live-casino": "casino",
		games: "casino",
		"free-quiz": "casino",
		poker: "casino",
		promotions: "promotions",
		"iframe-layout": "iframe",
	}[loc.pathname.split("/")[1]];
}

function setLastRouteType(loc) {
	let type = getRouteType(loc);
	if (type !== undefined) {
		this.props.dispatch(UISetLastRouteType(type));
	}
	this.props.dispatch(UISetCurrentRouteType(type || ""));
}

function getAuthDataForPartnerIntegration(hashParams) {
	return {
		auth_token: Helpers.getUriParam("AuthToken") || hashParams.AuthToken,
		user_id: Helpers.getUriParam("UserId") || hashParams.UserId,
	};
}

/**
 * InitMixin is a HOC(Higher Order Component) which adds init stuff to wrapped component
 *
 * component will wait for appReady to be set in store and then do the initialization,
 * which includes:
 *  - loading partner config from swarm
 *  - loading currency config from swarm
 *  - check if there's saved auth  token and try to log user in if it exists
 *  - set a timer to continuously prolong auth cookie lifetime while browser window is open
 *  - listen to route changes and close popup(if open) when route changes
 *
 * @param {Object} ComposedComponent
 * @constructor
 */

const InitMixin = ComposedComponent => {
	class InitMixin extends Component {
		initialize() {
			let key = Config.main.uiOpenKey;
			Config.main.title && (document.title = t(Config.main.title));
			key && this.props.dispatch(UIOpen(key));
			document.firstElementChild.setAttribute(
				"dir",
				Config.main.availableLanguages[Config.env.lang] &&
					Config.main.availableLanguages[Config.env.lang].rtl
					? "rtl"
					: "ltr"
			);

			const localeLangCode = langMapping[Config.env.lang] || Config.env.lang;

			moment.locale(localeLangCode);

			if (localeLangCode !== "en")
				try {
					import(`date-fns/locale/${localeLangCode}/index.js`).then(locale =>
						registerLocale(localeLangCode, locale)
					);
				} catch {}

			// Load partner config from swarm
			!Config.terminal.depositView && this.props.dispatch(LoadPartnerConfig()); //eslint-disable-line react/prop-types

			// Load currency config from swarm
			!Config.terminal.depositView && this.props.dispatch(LoadCurrencyConfig()); //eslint-disable-line react/prop-types

			// Login user if there's a saved auth token in cookies
			let storedAuthData = cookie.load("authData"),
				partnerIntegration = Config.isPartnerIntegration,
				hashParams = this.props.hashParams || {},
				{ dispatch } = this.props;

			(!storedAuthData || getAuthDataForPartnerIntegration(hashParams).auth_token) &&
				partnerIntegration &&
				partnerIntegration.mode &&
				(partnerIntegration.mode.iframe || partnerIntegration.needToLoginFromUrl) &&
				(storedAuthData = getAuthDataForPartnerIntegration(hashParams)) &&
				storedAuthData.auth_token &&
				prolongAuthCookieLifetime(storedAuthData, 60 * 60 * 1000);

			if (storedAuthData && storedAuthData.auth_token) {
				this.props.dispatch(RestoreLogin(storedAuthData)); //eslint-disable-line react/prop-types
			}
			if (
				Helpers.CheckIfPathExists(this.props, "preferences.geoData.countryCode") &&
				Helpers.CheckIfPathExists(
					Config,
					"main.regConfig.settings.WarningMessageForRestrictedIPPopup"
				) &&
				Config.main.regConfig.settings.WarningMessageForRestrictedIPPopup.indexOf(
					this.props.preferences.geoData.countryCode
				) !== -1
			) {
				this.props.dispatch(
					OpenPopup("message", {
						title: t("Warning"),
						type: "warning",
						body: t(
							"You cannot Sign Up from " +
								this.props.preferences.geoData.countryName
						),
					})
				);
			}
			function prolongAuthCookieLifetime(auth, liveTime) {
				let cookieAuth = cookie.load("authData");
				if (
					Config.main.clearSelectionsAfterLogOut &&
					auth &&
					cookieAuth &&
					auth.user_id !== cookieAuth.user_id
				) {
					dispatch(BetslipClear(true));
					dispatch(GamesFavoritesClear());
				}
				let authData = auth || cookie.load("authData");
				if (authData) {
					cookie.save("authData", authData, {
						path: "/",
						expires: new Date(
							Date.now() +
								(liveTime || Config.main.authSessionLifeTime) * 1000
						),
					});
				}
			}
			setInterval(prolongAuthCookieLifetime, 30000);

			browserHistory.listen(loc => {
				this.props.dispatch(ClosePopup(true)); //close popup when route changes(e.g. when clicking "back")
				setLastRouteType.call(this, loc);
			});
			routeHelper(hashParams, this);

			if (Object.keys(hashParams).length) {
				this.props.dispatch(StoreHashParams(hashParams));
			}

			if (
				Config.main.addBetSlipEventFromUrl &&
				hashParams &&
				((hashParams.game && hashParams.competition && hashParams.region && hashParams.event) ||
					hashParams.event)
			) {
				let fromUrl = true;
				this.props.dispatch(AddBetSlipEventFromUrl(hashParams, fromUrl));
			}

			/*if (hashParams.oddsType) {
                this.props.dispatch(changeOddsFormat(hashParams.oddsType))
            }*/
		}
		componentDidMount() {
			setLastRouteType.call(this, this.props.router.location);
			this.initialize();
		}

		UNSAFE_componentWillReceiveProps(nextProps) {
			if (Config.main.cloudAvailable && nextProps.cloudAvailable) {
				//Disable BetCloud when location anything else from prematch or live
				let pathname = this.props.router.location.pathname.split("/")[1];

				if (!(pathname === "prematch" || pathname === "live")) {
					this.props.dispatch(SwarmConfigData(false, "cloudAvailable"));
				}
			}
			//eslint-disable-next-line react/prop-types
			if (
				this.props.location &&
				this.props.location.pathname &&
				nextProps.location &&
				nextProps.location.pathname !== this.props.location.pathname
			) {
				// EverCookie logging on path change
				this.props.dispatch(UISetPreviousPath(this.props.location.pathname)); //eslint-disable-line react/prop-types
			}

			if (
				Config.main.birthDateAuthentication &&
				nextProps.user &&
				nextProps.user &&
				nextProps.user.reallyLoggedIn &&
				nextProps.user.profile &&
				nextProps.user.profile.birth_date
			) {
				if (nextProps.location.pathname.includes("casino")) {
					calculateBirthDateDiff(nextProps.user.profile.birth_date);
				}
			}
			// if (nextProps.profile && !this.haveProfile) {
			//     this.haveProfile = true;
			//
			// } else {
			//     this.haveProfile = false;
			// }
		}

		render() {
			return <ComposedComponent.Component {...this.props} {...this.state} />;
		}
	}
	InitMixin.propTypes = {
		user: PropTypes.object,
		router: PropTypes.object,
		preferences: PropTypes.object,
		betslip: PropTypes.object,
		hashParams: PropTypes.object,
		location: PropTypes.object,
		cloudAvailable: PropTypes.bool,
	};
	return connect(mapStateToProps)(InitMixin);
};

export default InitMixin;
